import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Rocket, Target, Zap, ShieldCheck, TrendingUp, Search, Star, MessageCircle, Layout, Smartphone, Bot, Clock, MousePointer2 } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Criar um Processo de Vendas IPTV no Piloto Automático", href: "/blog/como-criar-processo-vendas-piloto-automatico" }
  ];

  const sections = [
    {
      title: "Escala Real: O Fim do Atendimento Manual Exaustivo",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            O grande gargalo de um revendedor de IPTV em 2026 não é a falta de clientes, mas sim a <strong>falta de tempo</strong>. Atender cada interessado manualmente, enviar login de teste, explicar como instalar o app e cobrar renovações consome horas preciosas. Para se tornar um Master de elite, você precisa <strong>automatizar seu processo de vendas</strong>, criando uma engrenagem que funciona no piloto automático.
          </p>
          <p>
            Automatizar não significa ser impessoal. Significa utilizar a tecnologia para filtrar curiosos, educar o lead e entregar o produto (teste ou assinatura) instantaneamente. Com o suporte tecnológico da <strong>P2 PRO</strong> e ferramentas de automação de WhatsApp e Web, você pode fechar dezenas de vendas por dia enquanto foca na estratégia de crescimento do seu negócio.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-10 rounded-r-3xl relative overflow-hidden">
            <h4 className="text-white font-black mb-2 uppercase italic text-xl">Dica de Automação</h4>
            <p className="italic text-white/90">
              "Automatize 80% do seu processo (perguntas frequentes e entrega de testes) para que você possa dedicar 20% do seu tempo ao fechamento vip dos planos anuais."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Os 4 Pilares da Venda Automática",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "Atração de Tráfego 24h", d: "Use Google Ads e SEO para ter pessoas visitando seu site a qualquer hora do dia ou da noite.", i: <Search /> },
            { t: "Landing Page de Alta Conversão", d: "Um site que explica tudo, tira dúvidas e tem um botão direto para o teste automático.", i: <Layout /> },
            { t: "Bot de Atendimento (Chatbot)", d: "Configure fluxos no WhatsApp que qualificam o lead e entregam o login P2 PRO sem sua intervenção.", i: <Bot /> },
            { t: "Gestão de Renovação Automática", d: "Use as ferramentas do painel Master para enviar avisos de vencimento automáticos aos clientes.", i: <Clock /> },
            { t: "Pagamento com Pix Automatizado", d: "Integre sistemas que liberam a assinatura no painel assim que o cliente paga o Pix.", i: <Zap /> },
            { t: "Scripts de Up-sell Automático", d: "Mande mensagens programadas oferecendo bônus para quem migrar do plano mensal para o anual.", i: <TrendingUp /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Como Configurar seu Ecossistema Master",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            A base de tudo é o seu <strong>site Master</strong>. Ele deve ser o ponto central para onde todos os seus anúncios apontam. No site, o cliente vê o design dos apps One X e P2 PLAY, entende os planos e clica para testar. Ferramentas como Typebot ou ManyChat podem gerir essa conversa inicial no WhatsApp. Com o <strong>painel P2 PRO</strong>, você tem a segurança de uma infraestrutura estável que não exige intervenção técnica constante, facilitando a automação total.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <ShieldCheck className="text-electric w-8 h-8" /> Checklist da Escala Infinita
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <Target className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Qualificação Automática:</span> O bot deve perguntar qual o dispositivo do cliente antes de enviar o teste.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Smartphone className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Tutoriais em Vídeo:</span> Tenha links prontos de 'como instalar' para que o bot envie assim que o teste for gerado.</p>
              </li>
              <li className="flex gap-4 items-start">
                <MousePointer2 className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">CTA de Pagamento:</span> Após 2 horas de teste, o sistema deve enviar automaticamente o link de pagamento Pix.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Criar um processo de vendas no piloto automático em 2026 é o que permite que você saia da média e construa um <strong>faturamento de 5 ou 6 dígitos</strong>. Ao unir a tecnologia de ponta da <strong>P2 PRO</strong> com estratégias modernas de automação, você conquista a verdadeira liberdade financeira e de tempo que todo empreendedor Master deseja.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Como Revender Iptv"
      title="Como Criar Processo de Vendas IPTV no Piloto Automático"
      subtitle="Escalabilidade total para o seu negócio. Aprenda a montar uma estrutura que capta, atende e fecha vendas de streaming sem parar."
      heroImage="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitos revendedores estão presos no 'trabalho de formiguinha', atendendo um por um e perdendo horas em conversas que não levam a nada. Em 2026, a tecnologia permite que você tenha um exército digital trabalhando para você 24 horas por dia. É hora de parar de trabalhar PARA o seu negócio e começar a trabalhar NO seu negócio.",
        "Neste artigo, vamos detalhar as ferramentas e os fluxos necessários para automatizar desde a captação do lead no Google até a entrega da assinatura e a cobrança de renovação, tudo de forma integrada e eficiente.",
        "Com a parceria estratégica da P2 PRO, você tem a tranquilidade de uma infraestrutura que suporta grandes volumes de acessos simultâneos, garantindo que a sua máquina de vendas automática nunca pare por problemas técnicos no servidor ou nos aplicativos."
      ]}
      benefits={[
        { title: "Ganho de Tempo", description: "Libere sua agenda de tarefas repetitivas e foque em estratégias de marketing e expansão." },
        { title: "Escalabilidade Ilimitada", description: "Atenda 10 ou 1000 clientes com o mesmo nível de esforço e qualidade no atendimento." },
        { title: "Redução de Erros Humanos", description: "Evite esquecer de renovações ou de responder leads importantes com automações precisas." },
        { title: "Faturamento 24/7", description: "Receba pagamentos e ative assinaturas mesmo enquanto você está dormindo ou viajando." }
      ]}
      howItWorks={[
        { title: "Mapeamento de Fluxo", description: "Desenhe o caminho que o cliente faz desde o primeiro clique até o pagamento final." },
        { title: "Escolha das Ferramentas", description: "Selecione chatbots, criadores de landing pages e sistemas de pagamento automatizados." },
        { title: "Configuração de Scripts", description: "Escreva as mensagens automáticas que vão guiar o cliente em cada etapa do funil." },
        { title: "Testes e Otimização", description: "Monitore os resultados, veja onde os clientes estão parando e ajuste a automação para converter mais." }
      ]}
      diferenciais={[
        { title: "Painel Master P2 PRO", description: "Acesso a ferramentas de gestão que facilitam a integração com sistemas externos de automação." },
        { title: "Sinal P2P de Elite", description: "A segurança de automatizar um serviço que não gera reclamações por travamento." },
        { title: "Apps One X Luxuosos", description: "O visual que passa confiança instantânea na sua Landing Page automática." }
      ]}
      faq={[
        { question: "Automação não afasta o cliente?", answer: "Pelo contrário. Em 2026, o cliente valoriza a velocidade. Se for bem feita, a automação parece um atendimento vip e instantâneo." },
        { question: "É caro montar essa estrutura?", answer: "Existem ferramentas gratuitas ou de baixo custo para começar. O investimento se paga rapidamente com o aumento das vendas." },
        { question: "Preciso saber programar?", answer: "Não. A maioria das ferramentas de automação hoje é do tipo 'arrasta e solta', muito intuitivas para qualquer Master." },
        { question: "E se o bot não souber responder algo?", answer: "Sempre tenha uma opção de 'Falar com Atendente' para casos específicos onde o humano é necessário." },
        { question: "Qual a melhor ferramenta para WhatsApp?", answer: "Typebot integrado com API de WhatsApp é uma das soluções mais flexíveis e poderosas atualmente." },
        { question: "Como automatizar o recebimento Pix?", answer: "Utilize plataformas de checkout que geram o QR Code e avisam o seu sistema (ou bot) quando o pagamento cai." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Coloque sua Revenda no Piloto Automático"
      ctaDescription="Escala, lucro e liberdade com a tecnologia P2 PRO. Torne-se um Master de elite hoje mesmo."
      ctaButtonText="Ser um Revendedor Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-criar-processo-vendas-piloto-automatico")({
  head: () => ({
    meta: [
      { title: "Como Criar Processo de Vendas IPTV no Piloto Automático | P2 PRO" },
      { name: "description", content: "Aprenda a automatizar sua revenda de IPTV. Guia completo sobre chatbots, landing pages e processos automáticos para vender streaming em escala." },
      { name: "keywords", content: "automação de vendas iptv, chatbot para streaming, escala revenda iptv, processo de vendas automático" }
    ],
  }),
  component: ArticlePage,
});